import React, { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { twMerge } from "tailwind-merge";
import { useLoginContext } from "../../../hooks";
import { ClassroomCTAStatusType } from "../../../src/types";
import Loading from "./Loading";
import ConnectWallet from "./ConnectWallet";
import StartNext from "./StartNext";
import Completed from "./Completed";
import QuestSucceed from "../../Academy/QuestsSucceed";
import useUserStatus from "../../../hooks/user-status";

interface CallToActionProps {
  completed: number;
  totalQuests: number;
  nextQuestSlug: string;
  whatsNextLink: string;
  handleLogin: () => void;
  className?: string;
}

function CallToAction({
  completed,
  totalQuests,
  nextQuestSlug,
  whatsNextLink,
  handleLogin,
  className = "",
}: CallToActionProps) {
  const { t: tCommon } = useTranslation("common");
  const {
    isReadyToQuest,
    isLoading: isLoginLoading,
    isProcessing: isLoginProcessing,
  } = useLoginContext();
  const { isLoading: isUserStatusLoading } = useUserStatus();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<ClassroomCTAStatusType>("LOADING");

  useEffect(() => {
    setIsLoading(isLoginLoading || isLoginProcessing || isUserStatusLoading);
  }, [isLoginLoading, isLoginProcessing, isUserStatusLoading]);

  useEffect(() => {
    if (isLoading) {
      setStatus("LOADING");
    } else if (!isReadyToQuest) {
      setStatus("CONNECT_WALLET");
    } else if (completed === totalQuests) {
      setStatus("COMPLETED");
    } else {
      setStatus("START_NEXT");
    }
  }, [isReadyToQuest, isLoading, completed, totalQuests]);

  return (
    <div className={twMerge("my-10 w-full", className)}>
      <div className="flex items-center mb-2">
        <img
          className="w-6 h-6 ml-0.5 "
          src={status === "COMPLETED" ? "/icons/star.svg" : "/icons/power.svg"}
          alt="cta icon"
        />

        <span className="ml-3 display-7 md:display-6 uppercase text-purple">
          {status === "COMPLETED"
            ? tCommon("itsASuccess")
            : tCommon("letsPlay")}
        </span>
      </div>

      <div className="bg-purple-darker flex flex-col items-start gap-6 py-4 px-8 rounded-xl bodyText place-content-between">
        {status === "LOADING" ? null : (
          <QuestSucceed
            completed={completed}
            total={totalQuests}
            className="w-full"
          />
        )}

        {
          {
            LOADING: <Loading />,
            CONNECT_WALLET: <ConnectWallet handleLogin={handleLogin} />,
            START_NEXT: (
              <StartNext completed={completed} nextQuestSlug={nextQuestSlug} />
            ),
            COMPLETED: <Completed nextLink={whatsNextLink} />,
          }[status]
        }
      </div>
    </div>
  );
}

export default CallToAction;
